/**
 * Parse et construction des paramètres d'URL pour la recherche établissements.
 * Convention : listes multi-valeurs en `slug1,slug2` (pas de virgules dans les slugs).
 */

import type { LocationQueryRaw, LocationQueryValue } from 'vue-router'

import {
  findDomainOption,
  findFormationTypeOption,
  findLevelOption,
  findSchoolTypeOption,
} from '~/composables/establishmentSearchFilterLists'
import type { EstablishmentFilterOption } from '~/types/establishmentSearchFilterLists'
import type {
  EstablishmentActiveFilterChip,
  EstablishmentFilterQueryParam,
  EstablishmentSearchApiBody,
  EstablishmentSearchBBox,
  EstablishmentSearchDisplayKind,
  EstablishmentSearchFilterInput,
  StoredSearchBBox,
} from '~/types/EstablishmentSearch'
import type { EstablishmentSearchFiltersPayload } from '~/types/Tracking'

/** Bbox métropole + Corse — vue France par défaut (zoom ~6), utilisée en SSR avant interaction carte. */
export const FRANCE_DEFAULT_SEARCH_BBOX: EstablishmentSearchBBox = {
  southWestLat: 41.3253,
  southWestLng: -5.5591,
  northEastLat: 51.089,
  northEastLng: 9.6625,
}

const BBOX_COMPARE_EPSILON: number = 0.0001

/**
 * @param {EstablishmentSearchBBox} bbox - Bbox à comparer.
 * @returns {boolean} `true` si la bbox correspond à la vue France par défaut.
 */
export function isFranceDefaultEstablishmentSearchBBox(bbox: EstablishmentSearchBBox): boolean {
  return (
    Math.abs(bbox.southWestLat - FRANCE_DEFAULT_SEARCH_BBOX.southWestLat) < BBOX_COMPARE_EPSILON &&
    Math.abs(bbox.southWestLng - FRANCE_DEFAULT_SEARCH_BBOX.southWestLng) < BBOX_COMPARE_EPSILON &&
    Math.abs(bbox.northEastLat - FRANCE_DEFAULT_SEARCH_BBOX.northEastLat) < BBOX_COMPARE_EPSILON &&
    Math.abs(bbox.northEastLng - FRANCE_DEFAULT_SEARCH_BBOX.northEastLng) < BBOX_COMPARE_EPSILON
  )
}

/**
 * Libellés filtres actifs pour PostHog (même logique que le body API).
 * @param {EstablishmentSearchDisplayKind} displayKind - Mode page.
 * @param {EstablishmentSearchFilterInput} input - Filtres courants.
 * @returns {EstablishmentSearchFiltersPayload} Libellés par dimension.
 */
export function buildEstablishmentSearchFiltersLabelsPayload(
  displayKind: EstablishmentSearchDisplayKind,
  input: EstablishmentSearchFilterInput,
): EstablishmentSearchFiltersPayload {
  return {
    type: slugsToFilterLabels(input.typeSlugs, findSchoolTypeOption),
    domain: slugsToFilterLabels(input.domainSlugs, findDomainOption),
    level: slugsToFilterLabels(input.levelSlugs, findLevelOption),
    ftype:
      displayKind === 'program'
        ? slugsToFilterLabels(input.formationTypeSlugs, findFormationTypeOption)
        : [],
  }
}

/** Clés query effaçées par « tout effacer ». */
const ESTABLISHMENT_FILTER_QUERY_KEYS: readonly string[] = [
  'type',
  'domain',
  'level',
  'ftype',
  'q',
  'bbox',
  'mapSearch',
]

/** Source pour construire les chips de filtres actifs. */
interface EstablishmentFilterChipSource {
  readonly param: EstablishmentFilterQueryParam
  readonly slugs: readonly string[]
  readonly findOption: (slug: string) => EstablishmentFilterOption | undefined
}

/**
 * @param {LocationQueryRaw} query - Query courante.
 * @returns {LocationQueryRaw} Copie mutable.
 */
function cloneRouteQuery(query: LocationQueryRaw): LocationQueryRaw {
  return { ...query }
}

/**
 * @param {LocationQueryRaw} query - Query source.
 * @param {readonly string[]} keysToOmit - Clés à retirer.
 * @returns {LocationQueryRaw} Copie sans ces clés.
 */
function omitQueryParams(query: LocationQueryRaw, keysToOmit: readonly string[]): LocationQueryRaw {
  const omitSet = new Set(keysToOmit)
  return Object.fromEntries(
    Object.entries(query).filter(([key]) => !omitSet.has(key)),
  ) as LocationQueryRaw
}

/**
 * @param {LocationQueryRaw} query - Query cible.
 * @param {EstablishmentFilterQueryParam} param - Clé filtre.
 * @param {string | undefined} value - Valeur sérialisée ou `undefined` pour supprimer.
 * @returns {void}
 */
function setQueryParam(
  query: LocationQueryRaw,
  param: EstablishmentFilterQueryParam | 'q' | 'bbox' | 'mapSearch' | 'view' | 'displayKind',
  value: string | undefined,
): void {
  if (value !== undefined) query[param] = value
  else query[param] = undefined
}

/**
 * @param {LocationQueryRaw} query - Query cible.
 * @param {EstablishmentFilterQueryParam | 'q' | 'bbox' | 'mapSearch' | 'view' | 'displayKind'} param - Clé à retirer.
 * @returns {LocationQueryRaw} Copie sans cette clé.
 */
function withoutQueryParam(
  query: LocationQueryRaw,
  param: EstablishmentFilterQueryParam | 'q' | 'bbox' | 'mapSearch' | 'view' | 'displayKind',
): LocationQueryRaw {
  return omitQueryParams(query, [param])
}

/**
 * @param {LocationQueryValue | LocationQueryValue[] | undefined} raw - Valeur `route.query.*`.
 * @returns {string} Première valeur stringifiée ou chaîne vide.
 */
export function parseRouteQueryString(
  raw: LocationQueryValue | LocationQueryValue[] | undefined,
): string {
  if (raw === undefined || raw === null) return ''
  const first: LocationQueryValue | undefined = Array.isArray(raw) ? raw[0] : raw
  return first != null ? String(first) : ''
}

/**
 * @param {unknown} raw - Valeur brute `route.query.x`.
 * @returns {string[]} Liste de slugs non vides.
 */
export function parseCommaSlugList(raw: unknown): string[] {
  if (raw === undefined || raw === null || raw === '') return []
  const serialized: string = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw)
  return serialized
    .split(',')
    .map((part: string) => part.trim())
    .filter((part: string) => part.length > 0)
}

/**
 * @param {readonly string[]} slugs - Slugs à sérialiser.
 * @returns {string | undefined} Chaîne pour query ou `undefined` si vide.
 */
export function serializeCommaSlugList(slugs: readonly string[]): string | undefined {
  if (slugs.length === 0) return undefined
  return slugs.join(',')
}

/**
 * Parse `bbox=swLat,swLng,neLat,neLng`.
 * @param {unknown} raw - `route.query.bbox`.
 * @returns {EstablishmentSearchBBox | null} Bbox valide ou `null`.
 */
export function parseEstablishmentBBox(raw: unknown): EstablishmentSearchBBox | null {
  if (raw === undefined || raw === null || raw === '') return null
  const serialized: string = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw)
  const parts: string[] = serialized.split(',').map((part: string) => part.trim())
  if (parts.length !== 4) return null
  const southWestLat = Number.parseFloat(parts[0]!)
  const southWestLng = Number.parseFloat(parts[1]!)
  const northEastLat = Number.parseFloat(parts[2]!)
  const northEastLng = Number.parseFloat(parts[3]!)
  if (
    !Number.isFinite(southWestLat) ||
    !Number.isFinite(southWestLng) ||
    !Number.isFinite(northEastLat) ||
    !Number.isFinite(northEastLng)
  ) {
    return null
  }
  return { southWestLat, southWestLng, northEastLat, northEastLng }
}

/**
 * @param {EstablishmentSearchBBox} bbox - Zone carte.
 * @returns {string} Valeur query `bbox`.
 */
export function serializeEstablishmentBBox(bbox: EstablishmentSearchBBox): string {
  return `${bbox.southWestLat},${bbox.southWestLng},${bbox.northEastLat},${bbox.northEastLng}`
}

const STORED_SEARCH_BBOX_KEY: string = 'establishmentSearch.lastBBox'
const STORED_SEARCH_BBOX_TTL_MS: number = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Lit la dernière bbox de recherche mémorisée (localStorage), si encore fraîche.
 * @returns {EstablishmentSearchBBox | null} Bbox ou `null`.
 */
export function getStoredSearchBBox(): EstablishmentSearchBBox | null {
  if (!import.meta.client) {
    return null
  }
  const storedSearchBBox: string | null = localStorage.getItem(STORED_SEARCH_BBOX_KEY)
  if (!storedSearchBBox) {
    return null
  }
  const parsedStoredSearchBBox: StoredSearchBBox = JSON.parse(storedSearchBBox)
  if (Date.now() - parsedStoredSearchBBox.timestamp > STORED_SEARCH_BBOX_TTL_MS) {
    return null
  }
  return parseEstablishmentBBox(parsedStoredSearchBBox.bbox)
}

/**
 * Mémorise la dernière bbox de recherche (localStorage) pour le retour de l'utilisateur.
 * @param {EstablishmentSearchBBox} bbox - Zone à mémoriser.
 * @returns {void}
 */
export function setStoredSearchBBox(bbox: EstablishmentSearchBBox): void {
  if (!import.meta.client) return
  const payload: StoredSearchBBox = {
    bbox: serializeEstablishmentBBox(bbox),
    timestamp: Date.now(),
  }
  localStorage.setItem(STORED_SEARCH_BBOX_KEY, JSON.stringify(payload))
}

/**
 * @param {number} lat - Latitude point.
 * @param {number} lng - Longitude point.
 * @param {EstablishmentSearchBBox} bbox - Zone.
 * @returns {boolean} `true` si le point est dans le rectangle (approximation plate).
 */
export function isPointInBBox(lat: number, lng: number, bbox: EstablishmentSearchBBox): boolean {
  const minLat: number = Math.min(bbox.southWestLat, bbox.northEastLat)
  const maxLat: number = Math.max(bbox.southWestLat, bbox.northEastLat)
  const minLng: number = Math.min(bbox.southWestLng, bbox.northEastLng)
  const maxLng: number = Math.max(bbox.southWestLng, bbox.northEastLng)
  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng
}

/**
 * @param {unknown} raw - `route.query.mapSearch`.
 * @returns {boolean} `true` si activé (`1`, `true`, ou paramètre absent = défaut activé).
 */
export function parseMapSearchFlag(raw: unknown): boolean {
  if (raw === undefined || raw === null) return true
  const serialized: string = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw)
  if (serialized === '') return true
  if (serialized === '0' || serialized.toLowerCase() === 'false') return false
  return serialized === '1' || serialized.toLowerCase() === 'true'
}

/**
 * @param {unknown} raw - `route.query.view`.
 * @returns {'map' | 'list'} Vue mobile.
 */
export function parseEstablishmentView(raw: unknown): 'map' | 'list' {
  const serialized: string = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw ?? '')
  return serialized === 'map' ? 'map' : 'list'
}

/**
 * @param {object} params - Champs filtres (valeurs déjà résolues depuis la route / refs).
 * @param {string} params.q - Texte de recherche appliqué.
 * @param {readonly string[]} params.typeSlugs - Types d'école.
 * @param {readonly string[]} params.domainSlugs - Domaines.
 * @param {readonly string[]} params.levelSlugs - Niveaux.
 * @param {readonly string[]} params.formationTypeSlugs - Types de formation.
 * @returns {EstablishmentSearchFilterInput} Payload filtre pour le hub / l'API.
 */
export function buildEstablishmentFilterInput(params: {
  readonly q: string
  readonly typeSlugs: readonly string[]
  readonly domainSlugs: readonly string[]
  readonly levelSlugs: readonly string[]
  readonly formationTypeSlugs: readonly string[]
}): EstablishmentSearchFilterInput {
  return {
    q: params.q,
    typeSlugs: params.typeSlugs,
    domainSlugs: params.domainSlugs,
    levelSlugs: params.levelSlugs,
    formationTypeSlugs: params.formationTypeSlugs,
  }
}

/**
 * Convertit des slugs URL en libellés affichés (une option select = une string).
 * @param {readonly string[]} slugs - Slugs actifs dans la query.
 * @param {(slug: string) => EstablishmentFilterOption | undefined} findOption - Résolveur label.
 * @returns {string[]} Libellés pour le body API.
 */
export function slugsToFilterLabels(
  slugs: readonly string[],
  findOption: (slug: string) => EstablishmentFilterOption | undefined,
): string[] {
  const labels: string[] = []
  for (const slug of slugs) {
    const option: EstablishmentFilterOption | undefined = findOption(slug)
    if (option) labels.push(option.label)
  }
  return labels
}

/**
 * @param {readonly EstablishmentFilterChipSource[]} sources - Slugs + résolveur label par filtre.
 * @returns {EstablishmentActiveFilterChip[]} Chips à afficher.
 */
export function buildActiveFilterChips(
  sources: readonly EstablishmentFilterChipSource[],
): EstablishmentActiveFilterChip[] {
  const chips: EstablishmentActiveFilterChip[] = []
  for (const source of sources) {
    for (const slug of source.slugs) {
      const option = source.findOption(slug)
      if (option) {
        chips.push({ param: source.param, slug, label: option.label })
      }
    }
  }
  return chips
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {'map' | 'list'} view - Vue mobile cible.
 * @returns {LocationQueryRaw} Query avec `view` (supprimé si `list`).
 */
export function buildQueryWithMobileView(
  currentQuery: LocationQueryRaw,
  view: 'map' | 'list',
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  if (view === 'list') delete nextQuery.view
  else nextQuery.view = view
  return nextQuery
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {EstablishmentSearchDisplayKind} displayKind - `school` ou `program`.
 * @returns {LocationQueryRaw} Query avec `displayKind` (`ftype` retiré en mode école).
 */
export function buildQueryWithDisplayKind(
  currentQuery: LocationQueryRaw,
  displayKind: EstablishmentSearchDisplayKind,
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  nextQuery.displayKind = displayKind
  if (displayKind === 'school') delete nextQuery.ftype
  return nextQuery
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {EstablishmentFilterQueryParam} param - Clé filtre.
 * @param {readonly string[]} slugs - Slugs sélectionnés.
 * @returns {LocationQueryRaw} Query mise à jour.
 */
export function buildQueryWithFilterParam(
  currentQuery: LocationQueryRaw,
  param: EstablishmentFilterQueryParam,
  slugs: readonly string[],
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  setQueryParam(nextQuery, param, serializeCommaSlugList(slugs))
  return nextQuery
}

/**
 * @param {readonly string[]} currentSlugs - Slugs actifs pour un filtre.
 * @param {string} slugToRemove - Slug à retirer.
 * @returns {string[]} Slugs restants.
 */
export function buildSlugsAfterChipRemoval(
  currentSlugs: readonly string[],
  slugToRemove: string,
): string[] {
  return currentSlugs.filter((slug: string) => slug !== slugToRemove)
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @returns {LocationQueryRaw} Query sans filtres ni recherche ni carte.
 */
export function buildQueryClearingFilters(currentQuery: LocationQueryRaw): LocationQueryRaw {
  return omitQueryParams(cloneRouteQuery(currentQuery), ESTABLISHMENT_FILTER_QUERY_KEYS)
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {boolean} enabled - Case « rechercher sur la carte ».
 * @returns {LocationQueryRaw} Query avec `mapSearch` / sans `bbox` si désactivé.
 */
export function buildQueryWithMapSearch(
  currentQuery: LocationQueryRaw,
  enabled: boolean,
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  if (enabled) {
    nextQuery.mapSearch = '1'
    return nextQuery
  }
  nextQuery.mapSearch = '0'
  return withoutQueryParam(nextQuery, 'bbox')
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {EstablishmentSearchBBox} bbox - Zone carte.
 * @returns {LocationQueryRaw} Query avec `bbox` et `mapSearch=1`.
 */
export function buildQueryWithMapBounds(
  currentQuery: LocationQueryRaw,
  bbox: EstablishmentSearchBBox,
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  nextQuery.bbox = serializeEstablishmentBBox(bbox)
  nextQuery.mapSearch = '1'
  return nextQuery
}

/**
 * @param {LocationQueryRaw} currentQuery - Query actuelle.
 * @param {string} searchText - Texte recherché (vide = suppression de `q`).
 * @returns {LocationQueryRaw} Query avec `q` mis à jour.
 */
export function buildQueryWithSearchText(
  currentQuery: LocationQueryRaw,
  searchText: string,
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = cloneRouteQuery(currentQuery)
  const trimmed: string = searchText.trim()
  setQueryParam(nextQuery, 'q', trimmed.length > 0 ? trimmed : undefined)
  return nextQuery
}

/**
 * @param {string} language - Locale API (`fr` / `en` / `es`).
 * @param {EstablishmentSearchDisplayKind} displayKind - Mode page (pour `ftype`).
 * @param {EstablishmentSearchFilterInput} input - Filtres courants (slugs URL).
 * @param {EstablishmentSearchBBox} bbox - Zone carte active.
 * @returns {EstablishmentSearchApiBody} Corps JSON pour `POST /api/search/*`.
 */
export function buildEstablishmentSearchApiBody(
  language: string,
  displayKind: EstablishmentSearchDisplayKind,
  input: EstablishmentSearchFilterInput,
  bbox: EstablishmentSearchBBox,
): EstablishmentSearchApiBody {
  const q: string = input.q.trim()
  const typeLabels: string[] = slugsToFilterLabels(input.typeSlugs, findSchoolTypeOption)
  const domainLabels: string[] = slugsToFilterLabels(input.domainSlugs, findDomainOption)
  const levelLabels: string[] = slugsToFilterLabels(input.levelSlugs, findLevelOption)
  const ftypeLabels: string[] =
    displayKind === 'program'
      ? slugsToFilterLabels(input.formationTypeSlugs, findFormationTypeOption)
      : []

  return {
    language,
    bbox,
    ...(q.length > 0 ? { q } : {}),
    ...(typeLabels.length > 0 ? { type: typeLabels } : {}),
    ...(domainLabels.length > 0 ? { domain: domainLabels } : {}),
    ...(levelLabels.length > 0 ? { level: levelLabels } : {}),
    ...(ftypeLabels.length > 0 ? { ftype: ftypeLabels } : {}),
  }
}

/**
 * @param {EstablishmentSearchDisplayKind} displayKind - Mode page.
 * @param {EstablishmentSearchFilterInput} input - Filtres.
 * @param {number} fetchGeneration - Compteur pour refetch carte (zoom / appliquer zone).
 * @returns {string} Clé de cache `useAsyncData`.
 */
export function buildEstablishmentSearchCacheKey(
  displayKind: EstablishmentSearchDisplayKind,
  input: EstablishmentSearchFilterInput,
  fetchGeneration: number,
): string {
  // La bbox n'entre PAS dans la clé : elle est mise à jour silencieusement à chaque déplacement
  // carte et l'inclure orphelinerait la donnée (clé changée hors `watch`). `fetchGeneration`
  // (incrémenté à chaque recherche sur zone) suffit à invalider le cache.
  return [
    displayKind,
    String(fetchGeneration),
    input.q,
    input.typeSlugs.join(','),
    input.domainSlugs.join(','),
    input.levelSlugs.join(','),
    input.formationTypeSlugs.join(','),
  ].join('|')
}
